import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  TextField,
} from "@mui/material";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import "./Dashboard.css";
import AxiosInstance from "../../Services/AxiosInstance";
import { API_ENDPOINTS } from "../../Services/Services";
import { useAuth } from "../../ProtectedRoute/AppContext";
import logo from '../../assets/images/logo.png'; 
// Register chart components
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const Dashboard = () => {
  const { user } = useAuth();
  const [userCount, setUserCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [bugCount, setBugCount] = useState(0);
  const [recentActivities, setRecentActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [userActivityData, setUserActivityData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let userData, projectData, taskData, bugData;

        if (user.role === 1 || user.role === 2) {
          // Fetch data for Admin or higher roles
          [userData, projectData, taskData, bugData] = await Promise.all([
            AxiosInstance.get(API_ENDPOINTS.GET_USER_LIST),
            AxiosInstance.get(API_ENDPOINTS.GET_PROJECTS_REPORT),
            AxiosInstance.get(API_ENDPOINTS.GET_TASKS_REPORT),
            AxiosInstance.get(API_ENDPOINTS.GET_BUG_LIST),
          ]);
        } else {
          // Fetch data for specific users
          [userData, projectData, taskData, bugData] = await Promise.all([
            AxiosInstance.get(`${API_ENDPOINTS.GET_USER_BYID}?userId=${user.userId}`),
            AxiosInstance.get(`${API_ENDPOINTS.GET_PROJECTS_BYID}?userId=${user.sessionId}`),
            AxiosInstance.get(`${API_ENDPOINTS.GET_TASKS_BY_USERID}?userId=${user.userId}`),
            AxiosInstance.get(`${API_ENDPOINTS.GET_BUG_BY_USERID}?userId=${user.userId}`),
          ]);
        }

        // Update counts
        setUserCount(userData?.data?.length || 0);
        setProjectCount(projectData?.data?.length || 0);
        setTaskCount(taskData?.data?.length || 0);
        const bugs = bugData.data;

      // Calculate counts for each status
      const critical = bugs.filter((bug) => bug.priority === "Critical").length;
      const high = bugs.filter((bug) => bug.priority === "High").length;
      const medium = bugs.filter((bug) => bug.priority === "Medium").length;

      setBugCount({ critical, high, medium });

       // Process recent activities
       const activities = [];

       // Add user-related activities
       if (userData?.data) {
         userData.data.forEach((user) => {
           activities.push({
             type: "User",
             name: user.userName ,
             status: user.isActive ? "Active" : "Inactive",
           });
         });
       }
 
       // Add project-related activities
       if (projectData?.data) {
         projectData.data.forEach((project) => {
           activities.push({
             type: "Project",
             name: project.projectName ,
             status: project.status || "N/A", // Use appropriate status field
           });
         });
       }
 
       // Add task-related activities
       if (taskData?.data) {
         taskData.data.forEach((task) => {
           activities.push({
             type: "Task",
             name: task.taskName || task.title, // Adjust based on the actual field
             status: task.status || "N/A",
           });
         });
       }
 
       // Add bug-related activities
       if (bugData?.data) {
         bugData.data.forEach((bug) => {
           activities.push({
             type: "Bug",
             name: bug.bugName , // Adjust based on the actual field
             status: bug.priority || "N/A", // Use severity or status field
           });
         });
       }

       const shuffleArray = (array) => {
        for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
        }
      };
      
      // Shuffle the activities array
      shuffleArray(activities);
       
        setRecentActivities(activities);
        setFilteredActivities(activities);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [user.role]);

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    const filtered = recentActivities.filter((activity) =>
      activity.name.toLowerCase().includes(query)
    );
    setFilteredActivities(filtered);
  };

  const cardData = [
    { title: "Projects", count: projectCount, color: "#4caf50" },
    { title: "Tasks", count: taskCount, color: "#2196f3" },
    { title: "Bugs", count: bugCount ? bugCount.critical + bugCount.high + bugCount.medium : 0, color: "#f44336" },
    { title: "Users", count: userCount, color: "#ff9800" },
  ];
  

  const getDoughnutData = (count) => {
    return {
      datasets: [
        {
          data: [count, 30 - count],
          backgroundColor: ["#6a4cfc", "#e0e0e0"],
          borderWidth: 0,
        },
      ],
    };
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        color: "#000000",
        minHeight: "100vh",
        padding: 3,
      }}
    >
      <Grid container spacing={3}>
        {cardData.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper
              sx={{
                padding: 0,
                boxShadow: 3,
                height: 200,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-evenly",
                textAlign: "center",
              }}
            >
              <Typography variant="h6">{card.title}</Typography>
              <Box
                sx={{
                  width: "40%",
                  aspectRatio: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Doughnut data={getDoughnutData(card.count || 0)} />
              </Box>
              <Typography variant="h4" sx={{ color: card.color }}>
  {card.count || 0}
</Typography>

            </Paper>
          </Grid>
        ))}
      </Grid>
<div className="d-flex">
      <Box sx={{ marginTop: 4,
        maxHeight:500
       }} className="col-md-6 p-2 overflow-auto">
  <Paper sx={{ padding: 3, boxShadow: 3 }}>
    <Typography variant="h6" gutterBottom>
      Bug Status Breakdown
    </Typography>
    {bugCount && (
      <Bar
        data={{
          labels: ["Critical", "High", "Medium"],
          datasets: [
            {
              label: "Bug Count",
              data: [
                bugCount.critical || 0, // Replace with actual API data
                bugCount.high || 0,
                bugCount.medium || 0,
              ],
              backgroundColor: ["#f44336", "#ff9800", "#4caf50"],
            },
          ],
        }}
        height={100}
      />
    )}
  </Paper>
</Box>


      <Box sx={{ marginTop: 4, 
}}  className="col-md-6 p-2 ">
        <Paper sx={{ padding: 3, boxShadow: 3 }}>
          <Typography variant="h6" gutterBottom className="activitys">
            Recent Activities
          </Typography>
          <TextField
            fullWidth
            placeholder="Search Activities"
            onChange={handleSearch}
            sx={{ marginBottom: 2, 
              
             }}
          />
          <Divider sx={{ marginBottom: 2 }} />
          <List sx={{maxHeight:400}} className="overflow-auto">
            {filteredActivities.map((activity, index) => (
              <ListItem key={index} divider>
                <Avatar sx={{ bgcolor: "#6a4cfc", marginRight: 2 }}>
                  {activity.type[0]}
                </Avatar>
                <ListItemText
                  primary={`${activity.type}: ${activity.name}`}
                  secondary={`Status: ${activity.status}`}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box></div>
    </Box>
    
  );
};

export default Dashboard;
